import { Button, FormCheckbox, Icon, Modal } from 'scala'
import classnames from 'classnames'
import React, { useCallback, useContext, useMemo } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import styles from './modal-toggle-link.module.scss'
import { Context } from '../../context'

interface ModalToggleLinkProps {
  isOpen?: boolean
  loading: boolean
  enabled: boolean
  value?: string
  placeholder: string
  icon?: React.ReactNode
  showHeader?: boolean
  showCancelButton?: boolean
  showDoneButton?: boolean
  showCheckBox?: boolean
  showUrl?: boolean
  labels: {
    title: string
    toggleLabel: string
    toggleDescription?: string
    loading?: string
    cancelButton?: string
    copyButton?: string
    copyMessage?: string
    successToastTitle?: string
    successToastDescription?: string
  }
  onToggle(): void
  onDismiss?(): void
  onToggleMembersEditSetlist?(): void
  membersEditSetlist?: boolean
}

export const ModalToggleLink: React.FC<ModalToggleLinkProps> = ({
  isOpen,
  loading,
  enabled,
  value,
  placeholder,
  icon,
  showHeader,
  showCancelButton,
  showDoneButton,
  showUrl = true,
  showCheckBox,
  onToggleMembersEditSetlist,
  membersEditSetlist,
  labels,
  onToggle,
  onDismiss
}) => {
  const { i18n } = useLingui()
  const {
    toast: { add: addToast }
  } = useContext(Context)

  const {
    title,
    toggleLabel,
    toggleDescription,
    loading: loadingLabel,
    cancelButton,
    copyButton,
    copyMessage: copyMessagePrefix,
    successToastTitle,
    successToastDescription
  } = useMemo(() => {
    return {
      loading: i18n._(t`label.loading`),
      cancelButton: i18n._(t`actions.cancel`),
      copyButton: i18n._(t`copy_link`),
      successToastTitle: i18n._(t`link_copied`),
      ...labels
    }
  }, [i18n, labels])

  const url = loading ? `${i18n._(loadingLabel)}...` : value || placeholder

  const onCopyLink = useCallback(() => {
    const copyMessage = copyMessagePrefix ? `${copyMessagePrefix}\n${url}` : url

    navigator.clipboard.writeText(copyMessage).then(
      () => {
        onDismiss?.()
        addToast({
          icon: null,
          type: 'default',
          closable: true,
          ...(successToastDescription
            ? {
                title: i18n._(successToastTitle),
                description: i18n._(successToastDescription)
              }
            : { description: i18n._(successToastTitle) })
        })
      },
      () => {
        onDismiss?.()
        addToast({
          icon: null,
          type: 'error',
          closable: true,
          description: 'An error occurred, try again.'
        })
      }
    )
  }, [
    copyMessagePrefix,
    url,
    onDismiss,
    addToast,
    successToastDescription,
    i18n,
    successToastTitle
  ])

  return (
    <Modal
      targetId="moises-app" // TODO Check if this is necessary
      label={i18n._(title)}
      isOpen={isOpen || false}
      onClose={onDismiss}
      modalClassName={styles.modal}
      shouldCloseOnOverlayClick
    >
      <div className={styles.root}>
        {showHeader && (
          <>
            <div className={styles.header}>
              <div className={styles.title}>{title}</div>

              <Button
                transparent
                light
                className={styles.closeButton}
                icon={<Icon name="close" className={styles.closeIcon} />}
                onClick={onDismiss}
              />
            </div>

            <div className={styles.divider} />
          </>
        )}

        <div className={styles.content}>
          <div className={styles.toggle}>
            <div className={styles.label}>
              <p className={styles.title}>{i18n._(toggleLabel)}</p>

              {toggleDescription && (
                <p className={styles.description}>
                  {i18n._(toggleDescription)}
                </p>
              )}
            </div>

            <button
              type="button"
              className="not-as-button"
              onClick={onToggle}
              disabled={loading}
            >
              <FormCheckbox active={enabled} />
            </button>
          </div>

          {showUrl && (
            <div
              className={classnames(styles.link, {
                [styles.disabled]: !value || !enabled || loading
              })}
            >
              {icon && <span className={styles.icon}>{icon}</span>}
              <span className={styles.url}>{url}</span>
            </div>
          )}

          {showCheckBox && (
            <button
              id="allow_members_edit_checkbox"
              type="button"
              className={styles['checkbox-container']}
              onClick={onToggleMembersEditSetlist}
            >
              <div
                className={classnames(styles.checkbox, {
                  [styles.active]: membersEditSetlist
                })}
              >
                {membersEditSetlist && (
                  <Icon width={14} height={14} name="check-simple" />
                )}
              </div>
              <p className={styles.description}>
                {i18n._(t`setlist_allow_members_editing`)}
              </p>
            </button>
          )}
        </div>

        <div className={styles.actions}>
          {showCancelButton && (
            <Button
              small
              light
              stroke
              radius={1}
              transparent
              disabled={loading}
              title={i18n._(cancelButton)}
              className={classnames(styles.cancel, styles.button)}
              onClick={onDismiss}
            />
          )}

          <Button
            small
            stroke
            transparent
            radius={1}
            disabled={!enabled || loading || !value}
            onClick={onCopyLink}
            className={styles.button}
            title={i18n._(copyButton)}
            icon={<Icon name="link" width={20} height={20} />}
          />

          {showDoneButton && (
            <Button
              small
              primary
              radius={1}
              disabled={loading}
              title={i18n._(`done`)}
              className={styles.done}
              onClick={onDismiss}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
